(() => {
  let searchTimer = null;
  let searchAbort = null;

  const searchPanel = () => document.querySelector('[data-header-search]');
  const searchToggle = () => document.querySelector('[data-search-toggle]');
  const filterPanel = () => document.querySelector('[data-filter-panel]');
  const filterToggle = () => document.querySelector('[data-filter-toggle]');

  const setSearchOpen = (open) => {
    const panel = searchPanel();
    const toggle = searchToggle();
    if (!panel || !toggle) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      window.setTimeout(() => panel.querySelector('[data-header-search-input]')?.focus(), 80);
    } else {
      window.clearTimeout(searchTimer);
      if (searchAbort) {
        searchAbort.abort();
        searchAbort = null;
      }
    }
  };

  const setFilterOpen = (open) => {
    const panel = filterPanel();
    const toggle = filterToggle();
    if (!panel || !toggle) return;
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    toggle.setAttribute('aria-expanded', String(open));
    const label = toggle.querySelector('[data-filter-toggle-label]');
    if (label) label.textContent = open ? 'Collapse filters' : 'Filter';
  };

  const setSearchResultsVisible = (visible) => {
    const panel = searchPanel();
    if (!panel) return;
    const results = panel.querySelector('[data-header-search-results]');
    const input = panel.querySelector('[data-header-search-input]');
    if (results) results.hidden = !visible;
    if (input) input.setAttribute('aria-expanded', String(visible));
  };

  const clearSearchResults = () => {
    const panel = searchPanel();
    if (!panel) return;
    panel.querySelector('[data-header-search-grid]')?.replaceChildren();
    const status = panel.querySelector('[data-header-search-status]');
    if (status) status.textContent = '';
    setSearchResultsVisible(false);
  };

  const showMessage = (message, query = '') => {
    const panel = searchPanel();
    if (!panel) return;
    const grid = panel.querySelector('[data-header-search-grid]');
    const status = panel.querySelector('[data-header-search-status]');
    const all = panel.querySelector('[data-header-search-all]');
    if (!grid || !status || !all) return;
    grid.replaceChildren();
    const p = document.createElement('p');
    p.className = 'dlf-header-search__message';
    p.textContent = message;
    grid.appendChild(p);
    status.textContent = '';
    const base = panel.dataset.searchUrl || '/search';
    all.href = `${base}?q=${encodeURIComponent(query)}&type=product`;
    all.hidden = !query;
    setSearchResultsVisible(true);
  };

  const productImageUrl = (product) => {
    if (!product) return '';
    if (typeof product.image === 'string') return product.image;
    if (product.image && typeof product.image.url === 'string') return product.image.url;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && typeof product.featured_image.url === 'string') return product.featured_image.url;
    return '';
  };

  const renderProducts = (products, query) => {
    const panel = searchPanel();
    if (!panel) return;
    const grid = panel.querySelector('[data-header-search-grid]');
    const status = panel.querySelector('[data-header-search-status]');
    const all = panel.querySelector('[data-header-search-all]');
    if (!grid || !status || !all) return;
    grid.replaceChildren();

    if (!products.length) {
      showMessage(`No products found for “${query}”. Press Enter to search the full catalogue.`, query);
      return;
    }

    products.slice(0, 6).forEach((product) => {
      const link = document.createElement('a');
      link.className = 'dlf-search-result';
      link.href = product.url || `${panel.dataset.searchUrl || '/search'}?q=${encodeURIComponent(query)}&type=product`;

      const media = document.createElement('span');
      media.className = 'dlf-search-result__image';
      const src = productImageUrl(product);
      if (src) {
        const image = document.createElement('img');
        image.src = src;
        image.alt = '';
        image.loading = 'lazy';
        media.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'dlf-search-result__placeholder';
        media.appendChild(placeholder);
      }

      const copy = document.createElement('span');
      copy.className = 'dlf-search-result__copy';
      const title = document.createElement('span');
      title.className = 'dlf-search-result__title';
      title.textContent = product.title || 'Product';
      const meta = document.createElement('span');
      meta.className = 'dlf-search-result__meta';
      meta.textContent = product.vendor || product.product_type || product.type || 'DLF Merchanting';
      copy.append(title, meta);
      link.append(media, copy);
      grid.appendChild(link);
    });

    status.textContent = `${products.length} product${products.length === 1 ? '' : 's'} suggested`;
    const base = panel.dataset.searchUrl || '/search';
    all.href = `${base}?q=${encodeURIComponent(query)}&type=product`;
    all.hidden = false;
    setSearchResultsVisible(true);
  };

  const fetchSuggestions = async (query) => {
    const panel = searchPanel();
    if (!panel) return;
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();

    try {
      const base = panel.dataset.predictiveUrl || '/search/suggest';
      const endpoint = base.endsWith('.json') ? base : `${base}.json`;
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, signal: searchAbort.signal });
      if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      renderProducts(products, query);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      showMessage('Suggestions are temporarily unavailable. Press Enter to search the catalogue.', query);
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-search-toggle]');
    if (toggle) {
      const panel = searchPanel();
      const opening = !panel?.classList.contains('is-open');
      if (opening) setFilterOpen(false);
      setSearchOpen(opening);
      return;
    }

    if (event.target.closest('[data-header-search-close]')) {
      setSearchOpen(false);
      return;
    }

    if (event.target.closest('[data-header-search-clear]')) {
      const panel = searchPanel();
      const input = panel?.querySelector('[data-header-search-input]');
      const clear = panel?.querySelector('[data-header-search-clear]');
      if (input) {
        input.value = '';
        input.focus();
      }
      if (clear) clear.hidden = true;
      clearSearchResults();
      return;
    }

    if (event.target.closest('[data-filter-toggle]')) {
      const panel = filterPanel();
      const opening = !panel?.classList.contains('is-open');
      if (opening) setSearchOpen(false);
      setFilterOpen(opening);
      return;
    }

    if (event.target.closest('[data-filter-collapse]')) {
      setFilterOpen(false);
      filterToggle()?.focus();
      return;
    }

    const panel = searchPanel();
    const trigger = searchToggle();
    if (panel?.classList.contains('is-open') && !panel.contains(event.target) && !trigger?.contains(event.target)) {
      setSearchOpen(false);
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-header-search-input]');
    if (!input) return;
    const panel = searchPanel();
    const clear = panel?.querySelector('[data-header-search-clear]');
    const query = input.value.trim();
    if (clear) clear.hidden = query.length === 0;
    window.clearTimeout(searchTimer);
    if (searchAbort) {
      searchAbort.abort();
      searchAbort = null;
    }
    clearSearchResults();
    if (query.length < 2) return;
    searchTimer = window.setTimeout(() => fetchSuggestions(query), 180);
  });

  document.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      setFilterOpen(false);
      setSearchOpen(true);
      return;
    }

    if (event.key === 'Escape') {
      if (searchPanel()?.classList.contains('is-open')) {
        setSearchOpen(false);
        searchToggle()?.focus();
        return;
      }
      if (filterPanel()?.classList.contains('is-open')) {
        setFilterOpen(false);
        filterToggle()?.focus();
      }
      return;
    }

    if (event.key === 'ArrowDown' && event.target.matches('[data-header-search-input]')) {
      const first = searchPanel()?.querySelector('.dlf-search-result');
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-header-search-form]')) return;
    const input = event.target.querySelector('[data-header-search-input]');
    if (!input?.value.trim()) {
      event.preventDefault();
      input?.focus();
    }
  });

  const syncInitialState = () => {
    const panel = filterPanel();
    if (panel) setFilterOpen(panel.classList.contains('is-open'));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncInitialState, { once: true });
  else syncInitialState();
})();
